// How the edit sheet decides which SIP platform an existing trunk belongs to.
// Pure so the precedence is unit-testable without the sheet.

import {
  detectPlatformFromAclName,
  detectPlatformFromUri,
  SIP_PLATFORM_OPTIONS,
  type SipPlatformKey,
} from './platform-config'

/**
 * The inbound (Zentrunk) endpoints answer with the trunk EITHER at the top level
 * or wrapped as `{ success, trunk }`. Reading the ref straight off the wrapper
 * finds no id at all, so nothing gets recorded and it looks like the write is
 * broken (exactly what happened to inbound creation on dev, 2026-08-04).
 */
export const unwrapTrunk = <T extends { trunk_id?: string; uuid?: string; id?: number | string }>(
  payload?: T | { trunk?: T } | null,
): T | undefined => {
  if (!payload || typeof payload !== 'object') return undefined
  if ('trunk' in payload) return payload.trunk ?? undefined
  return payload as T
}

/**
 * The key a trunk's platform record is stored under. Read and write MUST derive
 * it the same way or a trunk records under one id and reads back under another,
 * which looks exactly like "the platform was never saved".
 *
 * Outbound trunks come from the Plivo API and carry `trunk_id`; inbound trunks
 * come from Zentrunk with a `uuid` and a numeric `id`. Same precedence as the
 * update call already uses to address a trunk.
 */
export const getTrunkRef = (trunk?: {
  trunk_id?: string
  uuid?: string
  id?: number | string
}): string | undefined => {
  if (!trunk) return undefined
  const ref = trunk.trunk_id || trunk.uuid || (trunk.id != null ? String(trunk.id) : '')
  return ref || undefined
}

export interface TrunkPlatformSources {
  /** What core recorded when the trunk was created or last saved. Authoritative. */
  recorded?: string
  /** Inbound trunks only: the stored primary URI, whose host encodes the platform. */
  primaryUri?: string
  /** Outbound trunks: name of the IP ACL attached, if any. */
  attachedAclName?: string
}

// Derived from the option list, not a hand-copy: a platform added there but
// missing here would silently demote every recorded trunk of that platform to
// URI/ACL inference (which happened to be safe only by luck).
const isKnownPlatform = (value: string): value is SipPlatformKey =>
  (SIP_PLATFORM_OPTIONS as string[]).includes(value)

/**
 * A record exists but this build does not know the value, which is different from
 * having no record at all. A newer console can record a platform this one has
 * never heard of; the resolution then falls back to inference so the UI still
 * works, but that fallback must never be written back or it destroys the newer,
 * authoritative record.
 */
export const isUnrecognizedRecordedPlatform = (recorded?: string): boolean => {
  const value = recorded?.trim()
  return !!value && !isKnownPlatform(value)
}

/**
 * Whether a save is allowed to WRITE the platform, which is a separate question
 * from what to display.
 *
 * The danger: until the recorded map arrives, the resolution falls back to
 * inference, and a credentials-only LiveKit trunk infers as `custom`. Recording
 * that would overwrite an authoritative `livekit` row with a guess and hand the
 * trunk back the IP ACL it cannot use, which is worse than the bug this all
 * exists to fix, because it destroys a correct record.
 *
 * So a write needs one of three justifications:
 * - creating: there is no record yet, so nothing can be clobbered, and the user
 *   picked the platform explicitly to get here,
 * - the user touched the field: an explicit choice outranks anything stored,
 * - the map loaded AND we understood what it said: replacing a value we can read
 *   is deliberate. A loaded-but-unrecognized record does NOT qualify, because the
 *   displayed value is then an inferred fallback, and writing it back would
 *   destroy a record a newer console build owns.
 *
 * Anything else (map pending, map failed, record present but unknown) means we do
 * not know, so we leave whatever is stored alone.
 */
export const canRecordPlatform = (state: {
  isEditing: boolean
  platformsLoaded: boolean
  platformTouched: boolean
  recordedUnrecognized?: boolean
}): boolean => {
  if (!state.isEditing) return true
  if (state.platformTouched) return true
  return state.platformsLoaded && !state.recordedUnrecognized
}

/**
 * Which platform to show for an existing trunk, in order of authority:
 *
 * 1. what core recorded (inference is a backfill, never an override, or
 *    attaching an ACL named `vapi-acl` would silently re-label a LiveKit trunk),
 * 2. a legacy trunk's own config: the inbound URI host, then the managed ACL name,
 * 3. custom. Every trunk predating the platform picker went through the old
 *    free-form flow, which IS custom, and the pre-redesign edit screen already
 *    behaved exactly that way.
 *
 * The result is always editable in the sheet: the console can re-point a trunk's
 * URI, ACL and credentials, so switching platform on an existing trunk is a real
 * migration path (Retell to Vapi keeps the trunk domain and its linked numbers),
 * and it is the only way to fix a trunk recorded wrong.
 */
export const resolveTrunkPlatform = (sources: TrunkPlatformSources): SipPlatformKey => {
  const recorded = sources.recorded?.trim()
  if (recorded && isKnownPlatform(recorded)) return recorded

  const fromUri = sources.primaryUri ? detectPlatformFromUri(sources.primaryUri) : null
  if (fromUri) return fromUri

  const fromAcl = detectPlatformFromAclName(sources.attachedAclName)
  if (fromAcl) return fromAcl

  return 'custom'
}
